import React, { useState } from 'react'
import clsx from 'classnames'
import type { CityService, ServiceGroup } from 'src/store/data.store'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { parseHtml } from 'utils/htmlParserWithCustomLinks'
import { formatPriceForSalon } from 'utils/formatPrice'
import { getServiceName } from 'utils/serviceOptions'
import { ServiceOptions } from 'components/PageElements/ServiceOptions/ServiceOptions'
import styles from './ServicesListItem.module.scss'

const MAX_AMOUNT = 10

type ServicesListItemProps = {
  service: CityService & { amount?: number },
  isButtonDisabled: boolean,
  isButtonSecondary: boolean,
  buttonTitle?: string,
  isSelected: boolean,
  /** Short view shows only name, duration and price (selected services in a collapsed category) */
  isShortView?: boolean,
  isAmountChangeable: boolean,
  salonId?: number | null,
  className?: string,
  durationUnits?: string,
  onButtonClick: (amount: number) => void,
  onAmountChange: (amount: number) => void,
  /** Options of the service's page, shown as a choice in the full view */
  options?: ServiceGroup['options'],
  activeOptionIdx?: number,
  onOptionChange?: (index: number) => void,
}

export const ServicesListItem: React.FC<ServicesListItemProps> = React.memo((props) => {
  const {
    service, isButtonDisabled, isButtonSecondary, isAmountChangeable, buttonTitle = 'Выбрать', isShortView = false,
    className, isSelected, salonId, durationUnits = 'мин', onButtonClick, onAmountChange,
    options, activeOptionIdx = 0, onOptionChange,
  } = props
  const showOptions = !isShortView && !!options && options.length > 1
  const [amount, setAmount] = useState(service.amount ?? 1)

  const changeAmount = (newAmount: number) => {
    if (newAmount <= 0 || newAmount > MAX_AMOUNT) {
      return
    }
    setAmount(newAmount)
    onAmountChange(newAmount)
  }

  const vars = service.meta?.templateVars?.pageVars
  // With the option chips shown the page title is enough, otherwise name the option too
  const name = showOptions ? service.meta?.title : getServiceName(service)
  const buttonShadowColor = isButtonSecondary || isButtonDisabled ? 'transparent' : 'pink'

  return (
    <div
      data-typeid={vars?.type}
      className={clsx({
        [styles.container]: true,
        [styles.short]: isShortView,
        [styles.selected]: isSelected,
        [className ?? '']: !!className,
      })}
    >
      <div className={styles.mainInfo}>
        <div className={styles.name}>
          <span className={styles.title}>{name}</span>
        </div>
        <div className={styles.attributes}>
          <div className={styles.durationContainer}>
            <div className={styles.duration}>{service.duration} {durationUnits}</div>
          </div>
          <div className={styles.priceContainer}>
            <div className={styles.priceBg}>
              {isAmountChangeable && <span className={styles.currency}>1 / </span>}
              <span>{formatPriceForSalon(service, salonId)}</span>
              <span className={styles.currency}>₽</span>
            </div>
          </div>
        </div>
      </div>
      {showOptions && (
        <ServiceOptions
          options={options}
          activeIndex={activeOptionIdx}
          onChange={(idx) => onOptionChange?.(idx)}
          salonId={salonId}
        />
      )}
      {!isShortView && <>
        <div className={styles.additionalInfo}>
          <div className={clsx([styles.description, 'text-content'])}>
            {parseHtml(vars?.description ?? '')}
          </div>
        </div>
        {isAmountChangeable && <div className={styles.amountContainer}>
          <div className={styles.amountReduce} onClick={() => changeAmount(amount - 1)} />
          <div className={styles.amount}>{amount}</div>
          <div className={styles.amountIncrease} onClick={() => changeAmount(amount + 1)} />
        </div>}
        <div className={styles.buttonContainer}>
          <AppButton
            shadowColor={buttonShadowColor}
            disabled={isButtonDisabled}
            onClick={() => onButtonClick(amount)}
            className={styles.button}
          >
            {buttonTitle}
          </AppButton>
        </div>
      </>}
    </div>
  )
})
ServicesListItem.displayName = 'ServicesListItem'
